import { useQueries, useQuery } from "@tanstack/react-query";
import { CircleSlash } from "lucide-react";

import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { asString, asStringArray } from "@/lib/tina/value-readers";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";
import { ParkLimitNotice } from "@/pages/tina/enrichment/components/park-limit-notice";
import { EntityCard } from "@/pages/tina/items/components/entity-card";
import { KeyValueGrid } from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import {
  ScoreBreakdownView,
  isScoreBreakdown,
} from "@/pages/tina/items/components/score-breakdown";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import {
  ENTITY_LABELS,
  labelFromKey,
} from "@/pages/tina/items/lib/entity-labels";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import type { PipelineItem } from "@/types/tina";

interface Entities {
  event_id?: string;
  venue_id?: string;
  artist_ids?: string[];
  [key: string]: unknown;
}

interface GenericEntity {
  key: string;
  label: string;
  id: string;
}

const HANDLED_ENTITY_KEYS = new Set(["event_id", "venue_id", "artist_ids"]);

export function DonePanel({ item }: { item: PipelineItem }) {
  const entities = (item.created_entities ?? {}) as Entities;
  const eventId = asString(entities.event_id) ?? item.created_event_id ?? null;
  const venueId = asString(entities.venue_id);
  const artistIds = asStringArray(entities.artist_ids);
  const genericEntities = getGenericEntities(entities);

  const eventRepo = useEventRepository();
  const venueRepo = useVenueRepository();
  // TODO: needs a bulk artist endpoint to avoid N+1 lookups here.
  const artistRepo = useArtistRepository();
  const scoreBreakdown = useScoreBreakdown(item.id);

  const eventQuery = useQuery({
    queryKey: ["events", eventId],
    queryFn: () => eventRepo.get(eventId as string),
    enabled: !!eventId,
  });

  const venueQuery = useQuery({
    queryKey: ["venues", venueId],
    queryFn: () => venueRepo.get(venueId as string),
    enabled: !!venueId,
  });

  const artistQueries = useQueries({
    queries: artistIds.map((id) => ({
      queryKey: ["artists", id],
      queryFn: () => artistRepo.get(id),
    })),
  });

  return (
    <PanelShell
      status="done"
      subtitle={`Outcome: ${item.final_outcome ?? "—"} · confidence ${item.final_confidence ?? "—"}`}
    >
      {/* Renders nothing unless `enrichment_result.park_limit_reached` is set
          — see ParkLimitNotice for why the flag, not the outcome, is the gate. */}
      <ParkLimitNotice item={item} />

      <KeyValueGrid
        rows={[
          { label: "Final outcome", value: item.final_outcome ?? "—" },
          { label: "Confidence", value: item.final_confidence ?? "—" },
        ]}
      />

      {scoreBreakdown.data && (
        <ScoreBreakdownView breakdown={scoreBreakdown.data} />
      )}

      <Separator />

      <div className="flex flex-col gap-3">
        {eventId && (
          <EntityCard
            label="Event"
            href={`/events/edit/${eventId}`}
            isLoading={eventQuery.isLoading}
            name={(eventQuery.data as { name?: string } | undefined)?.name}
            id={eventId}
          />
        )}
        {venueId && (
          <EntityCard
            label="Venue"
            href={`/venues/edit/${venueId}`}
            isLoading={venueQuery.isLoading}
            name={(venueQuery.data as { name?: string } | undefined)?.name}
            id={venueId}
          />
        )}
        {artistIds.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                Artists
              </h3>
              <span className="text-xs text-muted-foreground tabular-nums">
                {artistIds.length} matched
              </span>
            </div>
            <ul className="flex flex-col divide-y rounded-md border">
              {artistQueries.map((q, i) => {
                const id = artistIds[i];
                const name = (q.data as { name?: string } | undefined)?.name;
                return (
                  <li
                    key={id}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    {q.isLoading ? (
                      <Skeleton className="h-4 w-40" />
                    ) : (
                      <a
                        href={`/artists/edit/${id}`}
                        className="font-medium hover:underline underline-offset-2"
                      >
                        {name ?? id}
                      </a>
                    )}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {id}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {genericEntities.map((entity) => (
          <EntityCard
            key={`${entity.key}:${entity.id}`}
            label={entity.label}
            isLoading={false}
            name={entity.id}
            id={entity.id}
          />
        ))}
        {!eventId &&
          !venueId &&
          artistIds.length === 0 &&
          genericEntities.length === 0 && (
            <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <CircleSlash className="h-4 w-4" />
              No created entities recorded on this item.
            </p>
          )}
      </div>
    </PanelShell>
  );
}

/**
 * Fetch the score-stage breakdown for an item that passed score (MOO-4455).
 *
 * For HITL low_score items the breakdown rides along in hitl_context; for
 * passed items it only exists in the score stage's output artifact, so we
 * look it up lazily: stages → the `score` execution → its output artifact.
 * Returns null (no breakdown shown) when the item never ran score.
 */
function useScoreBreakdown(itemId: string) {
  const { getStages, getStageArtifacts } = useItemsRepository();

  return useQuery({
    queryKey: ["tina-items", itemId, "score-breakdown"],
    queryFn: async (): Promise<Record<string, unknown> | null> => {
      const stages = await getStages(itemId);
      const scoreStage = stages
        .filter((s) => s.stage === "score")
        .sort((a, b) => b.attempt - a.attempt)[0];
      if (!scoreStage) return null;

      const artifacts = await getStageArtifacts(itemId, scoreStage.id);
      const output = artifacts.artifacts.find(
        (a) => a.kind === "output" && isScoreBreakdown(a.content),
      );
      return output && isScoreBreakdown(output.content) ? output.content : null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

function getGenericEntities(entities: Entities): GenericEntity[] {
  return Object.entries(entities).flatMap(([key, value]) => {
    if (HANDLED_ENTITY_KEYS.has(key)) return [];

    if (
      import.meta.env?.DEV &&
      value !== undefined &&
      value !== null &&
      typeof value !== "string" &&
      typeof value !== "number" &&
      !Array.isArray(value)
    ) {
      console.warn(
        `DonePanel: created_entities.${key} has unexpected shape:`,
        value,
      );
    }

    return asStringArray(value).map((id) => ({
      key,
      label: ENTITY_LABELS[key] ?? labelFromKey(key),
      id,
    }));
  });
}
